'use client';

import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { useLocale } from 'next-intl';
import { type MouseEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { AuthenticationButton } from '@module/mdrt-org/shared/components/authentication-button/authentication-button';
import { ProfileDropdownItems } from '@module/mdrt-org/shared/components/header/components/profile-dropdown-items/profile-dropdown-items';
import { type HeaderAccountDropdownContent } from '@module/mdrt-org/shared/components/header/graphql/get-header-profile-dropdown-content';
import { LanguageSwitcher } from '@module/mdrt-org/shared/components/language-switcher/language-switcher';
import { type LanguagesContent } from '@module/mdrt-org/shared/utils/hooks/get-languages-content-cached';
import { ChevronRight } from '@shared/ui-components/svgs';
import { useMounted } from '@shared/utils/hooks/use-mounted';
import { usePathname } from '../../../../i18n/i18n-navigation';
import { Link } from '../../../../i18n/i18n-navigation-link';
import { useBreakpoint } from '../../../../utils/hooks/use-breakpoint';
import { type NavigationItem } from '../../../../utils/types/navigation-items';
import { getCurrentPageSubItemIds } from '../../utils/helpers/get-current-page-sub-item-ids';
import { useVisibleElement } from '../../utils/hooks/use-visible-element';
import { SubMenu } from './sub-menu/sub-menu';
import styles from './navigation.module.scss';
type NavigationProps = {
  close?: () => void;
  isAuthenticated: boolean;
  isMobile?: boolean;
  isSecondaryTheme?: boolean;
  languagesCmsContent?: LanguagesContent;
  navigationItems: NavigationItem[];
  profileDropdownCmsContent: HeaderAccountDropdownContent;
  profileSummarySlot?: ReactNode;
};
export const Navigation = t0 => {
  const $ = _c(67);
  let close;
  let isAuthenticated;
  let isMobile;
  let isSecondaryTheme;
  let languagesCmsContent;
  let navigationItems;
  let profileDropdownCmsContent;
  let profileSummarySlot;
  let props;
  if ($[0] !== t0) {
    ({
      close,
      isAuthenticated,
      isMobile,
      isSecondaryTheme,
      languagesCmsContent,
      navigationItems,
      profileDropdownCmsContent,
      profileSummarySlot,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = close;
    $[2] = isAuthenticated;
    $[3] = isMobile;
    $[4] = isSecondaryTheme;
    $[5] = languagesCmsContent;
    $[6] = navigationItems;
    $[7] = profileDropdownCmsContent;
    $[8] = profileSummarySlot;
    $[9] = props;
  } else {
    close = $[1];
    isAuthenticated = $[2];
    isMobile = $[3];
    isSecondaryTheme = $[4];
    languagesCmsContent = $[5];
    navigationItems = $[6];
    profileDropdownCmsContent = $[7];
    profileSummarySlot = $[8];
    props = $[9];
  }
  const locale = useLocale();
  const pathname = usePathname();
  const isMounted = useMounted();
  const [expandedMenuItem, setExpandedMenuItem] = useState(undefined);
  const observerRef = useRef(null);
  let t1;
  if ($[10] !== navigationItems || $[11] !== pathname) {
    t1 = getCurrentPageSubItemIds(navigationItems, pathname);
    $[10] = navigationItems;
    $[11] = pathname;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  const subItemIds = t1;
  const {
    isLargeDesktop
  } = useBreakpoint();
  const t2 = isMobile || !isLargeDesktop;
  let t3;
  if ($[13] !== subItemIds || $[14] !== t2) {
    t3 = {
      disabled: t2,
      elementIds: subItemIds
    };
    $[13] = subItemIds;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const [visibleSection, setPriorityOnSection] = useVisibleElement(t3);
  let t4;
  if ($[16] !== close || $[17] !== expandedMenuItem || $[18] !== isMobile) {
    t4 = (event, navigationItem) => {
      if (isMobile && navigationItem.subMenu && navigationItem.subMenu.length > 0) {
        event.preventDefault();
        setExpandedMenuItem(expandedMenuItem === navigationItem.title ? undefined : navigationItem.title);
      } else {
        close?.();
      }
    };
    $[16] = close;
    $[17] = expandedMenuItem;
    $[18] = isMobile;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const handleMenuItemClick = t4;
  let t5;
  if ($[20] !== close || $[21] !== setPriorityOnSection) {
    t5 = (_event, itemName) => {
      setPriorityOnSection(itemName);
      close?.();
    };
    $[20] = close;
    $[21] = setPriorityOnSection;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  const handleSubMenuItemClick = t5;
  let t6;
  if ($[23] !== subItemIds) {
    t6 = () => {
      const urlHash = window.location.hash;
      if (!urlHash) {
        return;
      }
      const isPageSection = subItemIds.includes(urlHash.replace("#", ""));
      if (!isPageSection) {
        return;
      }
      const scrollToSection = () => {
        const section = document.querySelector(urlHash);
        if (section) {
          section.scrollIntoView({
            behavior: "smooth"
          });
          observerRef.current?.disconnect();
        }
      };
      scrollToSection();
      const mainElement = document.querySelector("main");
      if (!mainElement) {
        return;
      }
      const observerCallback = mutationsList => {
        for (const mutation of mutationsList) {
          if (mutation.type === "childList" || mutation.type === "attributes") {
            scrollToSection();
          }
        }
      };
      const observer = new MutationObserver(observerCallback);
      observer.observe(mainElement, {
        childList: true,
        subtree: true
      });
      observerRef.current = observer;
      return () => observer.disconnect();
    };
    $[23] = subItemIds;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== isMounted || $[26] !== subItemIds) {
    t7 = [isMounted, subItemIds];
    $[25] = isMounted;
    $[26] = subItemIds;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  useEffect(t6, t7);
  const t8 = profileSummarySlot && isAuthenticated && profileSummarySlot;
  const t9 = !isAuthenticated;
  let t10;
  if ($[28] !== isSecondaryTheme || $[29] !== t9) {
    t10 = classNames(styles.navigation, {
      [styles["navigation--not-authenticated"]]: t9,
      [styles["navigation--secondary"]]: isSecondaryTheme
    });
    $[28] = isSecondaryTheme;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== expandedMenuItem || $[32] !== handleMenuItemClick || $[33] !== handleSubMenuItemClick || $[34] !== isMobile || $[35] !== navigationItems || $[36] !== pathname || $[37] !== visibleSection) {
    let t12;
    if ($[39] !== expandedMenuItem || $[40] !== handleMenuItemClick || $[41] !== handleSubMenuItemClick || $[42] !== isMobile || $[43] !== pathname || $[44] !== visibleSection) {
      t12 = navigationItem_0 => {
        const showDesktopSubMenu = navigationItem_0.subMenu && navigationItem_0.route === pathname;
        return <li className={styles["navigation__list-item"]} key={navigationItem_0.title}><Link aria-expanded={expandedMenuItem === navigationItem_0.title} className={classNames(styles["navigation__list-item-link"], {
            [styles["navigation__list-item-link--active"]]: pathname === navigationItem_0.route,
            [styles["navigation__list-item-link--expanded"]]: expandedMenuItem === navigationItem_0.title
          })} href={navigationItem_0.route} onClick={event_0 => handleMenuItemClick(event_0, navigationItem_0)}>{navigationItem_0.title}{isMobile && navigationItem_0.subMenu && navigationItem_0.subMenu.length > 0 && <ChevronRight className={classNames(styles["navigation__list-item-icon"], {
              [styles["navigation__list-item-icon--expanded"]]: expandedMenuItem === navigationItem_0.title
            })} />}</Link>{(isMobile || showDesktopSubMenu) && <SubMenu activeSubItem={visibleSection} isExpanded={expandedMenuItem === navigationItem_0.title} navItem={navigationItem_0} onClick={handleSubMenuItemClick} />}</li>;
      };
      $[39] = expandedMenuItem;
      $[40] = handleMenuItemClick;
      $[41] = handleSubMenuItemClick;
      $[42] = isMobile;
      $[43] = pathname;
      $[44] = visibleSection;
      $[45] = t12;
    } else {
      t12 = $[45];
    }
    t11 = navigationItems.map(t12);
    $[31] = expandedMenuItem;
    $[32] = handleMenuItemClick;
    $[33] = handleSubMenuItemClick;
    $[34] = isMobile;
    $[35] = navigationItems;
    $[36] = pathname;
    $[37] = visibleSection;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[46] !== t11) {
    t12 = <ul className={styles.navigation__list}>{t11}</ul>;
    $[46] = t11;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== close || $[49] !== isAuthenticated || $[50] !== isMobile || $[51] !== languagesCmsContent || $[52] !== locale || $[53] !== profileDropdownCmsContent) {
    t13 = isMobile && <ul className={classNames(styles["navigation__list-secondary"])}>{languagesCmsContent && <li><LanguageSwitcher languagesList={languagesCmsContent.languagesList} locale={locale} selectLanguageLabel={profileDropdownCmsContent.selectLanguageLabel} /></li>}{isAuthenticated && <ProfileDropdownItems cmsContent={profileDropdownCmsContent} isAuthenticated={isAuthenticated} onClose={close} />}</ul>;
    $[48] = close;
    $[49] = isAuthenticated;
    $[50] = isMobile;
    $[51] = languagesCmsContent;
    $[52] = locale;
    $[53] = profileDropdownCmsContent;
    $[54] = t13;
  } else {
    t13 = $[54];
  }
  let t14;
  if ($[55] !== props || $[56] !== t10 || $[57] !== t12 || $[58] !== t13) {
    t14 = <nav className={t10} {...props}>{t12}{t13}</nav>;
    $[55] = props;
    $[56] = t10;
    $[57] = t12;
    $[58] = t13;
    $[59] = t14;
  } else {
    t14 = $[59];
  }
  let t15;
  if ($[60] !== isAuthenticated || $[61] !== isMobile) {
    t15 = !isAuthenticated && isMobile && <div className={styles.navigation__authentication}><AuthenticationButton isAuthenticated={isAuthenticated} isMobile={true} /></div>;
    $[60] = isAuthenticated;
    $[61] = isMobile;
    $[62] = t15;
  } else {
    t15 = $[62];
  }
  let t16;
  if ($[63] !== t14 || $[64] !== t15 || $[65] !== t8) {
    t16 = <div className={styles["navigation-wrapper"]}>{t8}{t14}{t15}</div>;
    $[63] = t14;
    $[64] = t15;
    $[65] = t8;
    $[66] = t16;
  } else {
    t16 = $[66];
  }
  return t16;
};